import { FeatureFlags } from "@utils/FeatureFlags/generated-flags";
import { getActiveTemplateOptions } from "@utils/FeatureFlags/selfServeAccess";
import { buildTemplateSelectItemsUnfiltered } from "@utils/helpers";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Badge, Dialog, Paragraph, Select, SelectItem, Small } from "@fronterahealth/frontera-ui-components";

import {
  ApiReportTemplateReportTypeChoices,
  ReportTemplateStatus,
  ReportTemplateTypeEdge,
  useDuplicateReportTemplateMutation,
  useReportTemplatesQuery,
} from "@api/graphql/types-and-hooks";
import { notifyError } from "@components/notifications/notifications";

interface CreateTemplateDialog {
  setDialogOpen: (open: boolean) => void;
  isDialogOpen: boolean;
}

enum TemplateCreationType {
  InitialAssessment = "Initial Assessment",
  Reassessment = "Reassessment",
  DiagnosisEvaluation = "Diagnosis",
}

interface TemplateOption {
  id: ApiReportTemplateReportTypeChoices;
  title: TemplateCreationType;
  description: string;
  recommendedTags: string[];
}

const templateOptions: TemplateOption[] = [
  {
    id: ApiReportTemplateReportTypeChoices.InitialAssessment,
    title: TemplateCreationType.InitialAssessment,
    description: "Create a template for initial client assessments.",
    recommendedTags: ["Onboarding new clients"],
  },
  {
    id: ApiReportTemplateReportTypeChoices.Reassessment,
    title: TemplateCreationType.Reassessment,
    description: "Create a template for recurring client evaluations.",
    recommendedTags: ["Manual Intake", "Backfilling existing clients"],
  },
  {
    id: ApiReportTemplateReportTypeChoices.DiagnosisEvaluation,
    title: TemplateCreationType.DiagnosisEvaluation,
    description: "Create a template for diagnosis builder",
    recommendedTags: ["Manual Intake", "Backfilling existing clients"],
  },
];

export const CreateTemplateDialog: React.FC<CreateTemplateDialog> = ({ isDialogOpen, setDialogOpen }) => {
  const navigate = useNavigate();
  const flags = useFlags<FeatureFlags>();
  const [selectedTemplateType, setSelectedTemplateType] = useState<ApiReportTemplateReportTypeChoices | null>(
    ApiReportTemplateReportTypeChoices.InitialAssessment,
  );
  const [showError, setShowError] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<SelectItem>();

  const duplicateReportTemplate = useDuplicateReportTemplateMutation();
  const activeOptions = getActiveTemplateOptions(flags);

  const handleContinue = async () => {
    if (!selectedTemplateType) {
      setShowError(true);
      return;
    }

    // duplicate the selected report template
    await duplicateReportTemplate.mutateAsync(
      {
        input: {
          reportTemplateId: selectedTemplate?.id || "",
          // This dialog only ever runs inside the self-serve module, so intent is always
          // self-serve — assert it explicitly so the API doesn't misinfer it from an
          // internal/admin account copying on behalf of the org (root cause of PROA-1729).
          isSelfServeUser: true,
        },
      },
      {
        onSuccess: async (response) => {
          if (!response.duplicateReportTemplate?.status) {
            notifyError(response.duplicateReportTemplate?.message || "Could not duplicate report template.");
            return;
          }

          navigate(`/templates/${response.duplicateReportTemplate.newTemplate?.id}`);
          setDialogOpen(false);
        },
        onError: async (error: unknown) => {
          console.error("Error when duplicating report template", error);
          notifyError("Could not duplicate report template.");
        },
      },
    );
  };

  const handleRadioChange = (option: TemplateOption) => {
    setSelectedTemplateType(option.id);
    setSelectedTemplate(undefined);
  };

  const { data: templatesData, isLoading: templatesLoading } = useReportTemplatesQuery(
    {
      filter: {
        reportType: selectedTemplateType,
        statuses: [ReportTemplateStatus.Published],
      },
    },
    {
      queryKey: ["get-report-templates-by-type", selectedTemplateType],
    },
  );

  const templateEdges = templatesData?.getReportTemplates?.edges;

  const sortedTemplateSelections = [
    ...buildTemplateSelectItemsUnfiltered(templateEdges as ReportTemplateTypeEdge[]),
  ].sort((a, b) => {
    if (a.secondary === "Frontera" && b.secondary !== "Frontera") return -1;
    if (b.secondary === "Frontera" && a.secondary !== "Frontera") return 1;
    return 0;
  });

  // Auto-select the first template when templates load, preferring Frontera templates
  useEffect(() => {
    if (selectedTemplateType && sortedTemplateSelections.length > 0 && !selectedTemplate) {
      const fronteraTemplate = sortedTemplateSelections.find((template) => template.secondary === "Frontera");
      setSelectedTemplate(fronteraTemplate ?? sortedTemplateSelections[0]);
    }
  }, [selectedTemplateType, sortedTemplateSelections, selectedTemplate]);

  return (
    <>
      <Dialog
        title="Create New Template"
        open={isDialogOpen}
        setOpen={setDialogOpen}
        primaryButton={{
          text: "Continue",
          onClick: handleContinue,
          disabled: !selectedTemplateType,
        }}
        secondaryButton={{ text: "Cancel", onClick: () => setDialogOpen(false) }}
        overflowBehavior="show"
        dialogClassName="max-w-[549px]!"
      >
        <div className="flex flex-col gap-4">
          {templateOptions.map((option) => (
            <label
              key={option.id}
              className={`w-full border-2 p-4 rounded-lg flex justify-between items-center gap-4 cursor-pointer transition-all ${
                selectedTemplateType === option.id ? "border-green-700" : "border-limestone-50"
              }`}
            >
              <div className={`flex flex-col grow ${!activeOptions.includes(option.id) && "opacity-50"}`}>
                <div className="flex w-full items-end justify-between">
                  <div className="flex flex-col gap-4">
                    <Small displayType="loud" colorType="primary">
                      {option.title}
                    </Small>
                    <Paragraph colorType="primary" size="small">
                      {option.description}
                    </Paragraph>
                    {selectedTemplateType === option.id && (
                      <Paragraph size="small" colorType="primary">
                        Choose a starting point
                      </Paragraph>
                    )}
                  </div>
                </div>
                <div>
                  {selectedTemplateType === option.id && (
                    <Select
                      className="w-full mb-[-28px]"
                      title=""
                      items={sortedTemplateSelections}
                      placeholderText={"Select Template"}
                      selected={selectedTemplate}
                      setSelected={setSelectedTemplate}
                      isLoading={templatesLoading}
                    />
                  )}
                </div>
              </div>
              {activeOptions.includes(option.id) ? (
                <input
                  type="radio"
                  name="clientType"
                  value={option.id}
                  checked={selectedTemplateType === option.id}
                  onChange={() => handleRadioChange(option)}
                  className="w-5 h-5 text-green-700 checked:ring-0 focus:ring-0"
                />
              ) : (
                <div className="self-start">
                  <Badge appearance="warn" text="Coming Soon"></Badge>
                </div>
              )}
            </label>
          ))}
          {showError && <Small className="text-red-500">⚠️ Please select at least one option before continuing.</Small>}
        </div>
      </Dialog>
    </>
  );
};
